import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, ChevronDown, Building2, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth, roleLabels } from '@/lib/auth';
import { api } from '@/lib/api';
import { fmtRel } from '@/lib/format';

export function TopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
  const [openNotifs, setOpenNotifs] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const timer = useRef(null);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 12 } });
      setNotifs(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    loadNotifs();
    timer.current = setInterval(loadNotifs, 60_000);
    return () => clearInterval(timer.current);
  }, []);

  const toggleDark = () => {
    const el = document.documentElement;
    const isDark = el.classList.toggle('dark');
    setDark(isDark);
    localStorage.setItem('ad_theme', isDark ? 'dark' : 'light');
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    loadNotifs();
  };

  return (
    <div className="sticky top-0 z-30 h-14 border-b border-border bg-background/95 backdrop-blur flex items-center gap-3 px-4" data-testid="top-bar">
      <div className="flex-1 max-w-xl">
        <Input
          data-testid="global-search-input"
          placeholder="Search assets, alerts, incidents…"
          className="h-9 bg-secondary/60 border-border"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value.trim()) {
              nav(`/app/search?q=${encodeURIComponent(e.target.value.trim())}`);
            }
          }}
        />
      </div>
      <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="toggle-theme" aria-label="Toggle theme">
        {dark ? <Sun size={18} /> : <Moon size={18} />}
      </Button>

      <DropdownMenu open={openNotifs} onOpenChange={setOpenNotifs}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
            <Bell size={18} />
            {notifs.unread_count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono flex items-center justify-center">
                {notifs.unread_count}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="text-sm font-semibold">Notifications</div>
            <button className="text-xs text-primary hover:underline" onClick={markAllRead} data-testid="mark-all-read">
              Mark all read
            </button>
          </div>
          <DropdownMenuSeparator />
          <div className="max-h-96 overflow-y-auto">
            {notifs.items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</div>
            ) : (
              notifs.items.map((n) => (
                <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-0.5 py-2"
                                  onClick={async () => { await api.post(`/notifications/${n.id}/read`); loadNotifs(); if (n.link) nav(n.link); }}>
                  <div className="flex items-center gap-2 w-full">
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                    <div className="text-sm font-medium truncate flex-1">{n.title}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtRel(n.created_at)}</div>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                </DropdownMenuItem>
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-9 pl-2 pr-3 gap-2" data-testid="user-menu-trigger">
            <div className="h-6 w-6 rounded-full bg-primary/20 text-primary text-xs font-semibold flex items-center justify-center">
              {(user?.full_name || user?.email || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="text-left leading-tight hidden sm:block">
              <div className="text-xs font-medium">{user?.full_name}</div>
              <div className="text-[10px] text-muted-foreground">{roleLabels[user?.role] || user?.role}</div>
            </div>
            <ChevronDown size={14} className="opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={async () => { await logout(); nav('/login'); }} data-testid="logout-btn">
            <LogOut size={14} className="mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
